import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Glasses, History, Loader2 } from 'lucide-react';
import { HudHint, HudMessage } from '../../services/hud';
import {
  KIND_STYLE,
  PRIORITY_RANK,
  formatCountdown,
  formatTime,
  isBuildQuestion,
  isWaiting,
  messageOutcome,
  normaliseKind,
  toMs,
} from './hudShared';

const GLASSES_SLOTS = 3;

export function isPendingMessage(m: HudMessage): boolean {
  return m.messageId.startsWith('tmp-');
}

// ── Waiting ─────────────────────────────────────────────────────────────────

export function WaitingMessages({ messages, now }: { messages: HudMessage[]; now: number }) {
  if (messages.length === 0) return null;
  return (
    <section className="bg-white border border-amber-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-amber-100 bg-amber-50/60 flex items-center gap-2">
        <h2 className="text-[11px] font-bold text-amber-700 tracking-widest uppercase">Waiting for the lead</h2>
        <span className="ml-auto text-xs font-semibold text-amber-700">{messages.length}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {messages.map(m => {
          const pending = isPendingMessage(m);
          return (
            <div key={m.messageId} className="px-4 py-3 flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy leading-snug">{m.text}</p>
                <p className="text-xs text-slate-400">
                  {pending ? 'Sending...' : `Sent by ${m.sentByName || m.sentBy} at ${formatTime(m.createdAt)}${m.deliveredAt ? ', on the glasses' : ''}`}
                </p>
              </div>
              {pending ? (
                <Loader2 size={16} className="text-amber-600 animate-spin shrink-0" />
              ) : (
                <span className="text-sm font-bold text-amber-700 tabular-nums shrink-0" title="Time left before it expires">
                  {formatCountdown(toMs(m.expiresAt) - now)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── On the glasses now ──────────────────────────────────────────────────────

interface GlassesItem {
  key: string;
  kindLabel: string;
  kindBadge: string;
  text: string;
  detail: string;
  priority: string;
  createdAt: number;
  quietVisible: boolean;
}

function GlassesRow({ item, index, muted, tag }: { item: GlassesItem; index?: number; muted: boolean; tag?: string }) {
  const [open, setOpen] = useState(false);
  const hasDetail = !!item.detail.trim() && item.detail.trim() !== item.text.trim();
  return (
    <div className={`px-4 py-2.5 flex flex-col gap-1.5 ${muted ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-2.5">
        <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${
          index !== undefined ? 'bg-navy text-white' : 'bg-slate-100 text-slate-400'
        }`}>
          {index !== undefined ? index : '·'}
        </span>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border shrink-0 ${item.kindBadge}`}>
          {item.kindLabel}
        </span>
        {item.priority === 'high' && <span className="text-[10px] font-bold uppercase text-red-600 shrink-0 mt-0.5">High</span>}
        <p className="flex-1 text-sm text-navy leading-snug">{item.text}</p>
        {tag && <span className="text-[10px] font-bold uppercase text-slate-400 shrink-0 mt-0.5">{tag}</span>}
        {hasDetail && (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="text-slate-400 hover:text-navy shrink-0 mt-0.5"
            aria-label={open ? 'Hide detail' : 'Show detail'}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}
      </div>
      {open && hasDetail && (
        <p className="ml-7 text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">{item.detail}</p>
      )}
    </div>
  );
}

export function OnGlassesNow({ hints, waiting, quiet }: { hints: HudHint[]; waiting: HudMessage[]; quiet: boolean }) {
  const items = useMemo<GlassesItem[]>(() => {
    const fromHints = hints.filter(h => h.status === 'active').map(h => {
      const k = KIND_STYLE[normaliseKind(h.kind)];
      return {
        key: `hint-${h.hintId}`,
        kindLabel: isBuildQuestion(h) ? 'Build question' : k.label,
        kindBadge: k.badge,
        text: h.text,
        detail: h.detail || '',
        priority: h.priority || 'normal',
        createdAt: toMs(h.raisedAt),
        quietVisible: normaliseKind(h.kind) === 'message' || isBuildQuestion(h),
      };
    });
    const fromMessages = waiting.filter(m => !m.messageId.startsWith('tmp-')).map(m => ({
      key: `msg-${m.messageId}`,
      kindLabel: KIND_STYLE.message.label,
      kindBadge: KIND_STYLE.message.badge,
      text: m.text,
      detail: m.detail || '',
      priority: 'high',
      createdAt: toMs(m.createdAt),
      quietVisible: true,
    }));
    return [...fromHints, ...fromMessages].sort((a, b) =>
      (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1) || b.createdAt - a.createdAt);
  }, [hints, waiting]);

  const shown = quiet ? items.filter(i => i.quietVisible) : items;
  const paused = quiet ? items.filter(i => !i.quietVisible) : [];
  const top = shown.slice(0, GLASSES_SLOTS);
  const queued = shown.slice(GLASSES_SLOTS);

  return (
    <section className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#e2e8f0] flex items-center gap-2">
        <Glasses size={14} className="text-primary" />
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">On the glasses now</h2>
        <span className="ml-auto text-xs text-slate-400">{shown.length} showing</span>
      </div>
      {quiet && (
        <p className="px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50/60 border-b border-amber-100">
          Quiet mode: only messages and build questions are showing
        </p>
      )}
      {items.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-400">Nothing on the glasses.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {top.map((item, i) => <GlassesRow key={item.key} item={item} index={i + 1} muted={false} />)}
          {queued.length > 0 && (
            <p className="px-4 py-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/60">
              +{queued.length} queued
            </p>
          )}
          {queued.map((item, i) => <GlassesRow key={item.key} item={item} index={GLASSES_SLOTS + i + 1} muted />)}
          {paused.map(item => <GlassesRow key={item.key} item={item} muted tag="Paused" />)}
        </div>
      )}
    </section>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

const OUTCOME_PILL = {
  yes:     { label: 'Yes',      cls: 'bg-emerald-100 text-emerald-700' },
  no:      { label: 'No',       cls: 'bg-rose-100 text-rose-700' },
  expired: { label: 'No reply', cls: 'bg-slate-100 text-slate-500' },
} as const;

export function MessageHistory({ messages, now }: { messages: HudMessage[]; now: number }) {
  const done = useMemo(
    () => messages
      .filter(m => !isPendingMessage(m) && !isWaiting(m, now))
      .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)),
    [messages, now],
  );

  return (
    <section className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#e2e8f0] flex items-center gap-2">
        <History size={14} className="text-primary" />
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Message history</h2>
        <span className="ml-auto text-xs text-slate-400">{done.length}</span>
      </div>
      {done.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-400">No answered messages yet.</p>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {done.map(m => {
            const outcome = messageOutcome(m);
            const pill = OUTCOME_PILL[outcome];
            const from = toMs(m.deliveredAt) || toMs(m.createdAt);
            const answered = toMs(m.answeredAt);
            return (
              <div key={m.messageId} className="px-4 py-2.5 flex flex-col gap-0.5">
                <div className="flex items-start gap-3">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${pill.cls}`}>{pill.label}</span>
                  <p className="flex-1 text-sm text-navy leading-snug">{m.text}</p>
                </div>
                <p className="ml-1 text-xs text-slate-400">
                  Sent by {m.sentByName || m.sentBy} at {formatTime(m.createdAt)}
                  {outcome !== 'expired' && m.answeredBy && `, answered by ${m.answeredBy}`}
                  {outcome !== 'expired' && answered > 0 && from > 0 && answered >= from && `, answered in ${formatCountdown(answered - from)}`}
                  {outcome === 'expired' && `, expired ${formatTime(m.expiresAt)}`}
                </p>
                {m.detail && <p className="ml-1 text-xs text-slate-500 whitespace-pre-wrap">{m.detail}</p>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
